import React, { useEffect, Suspense } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, generatePath, useLocation, Switch, Route, Redirect } from 'react-router-dom';
import { Breadcrumbs, Typography, Tabs, Grid, AppBar } from '@mui/material';
import UmbrellaFoodDonorEditForm from '../components/UmbrellaFoodDonorEditForm';
import * as sitesActions from '../actions/sites';
import * as foodDonorsActions from '../actions/foodDonors';
import * as umbrellaFoodDonorsActions from '../actions/umbrellaFoodDonors';
import routes from '../routes';
import { TabLink } from '../components/Common/BottomNavTabs';
import Loading from '../components/Loading';
import FutureRescuesListView, { RescuesFromSiteFilterTypes } from './FutureRescuesListView';
import PastRescuesListView from './PastRescuesListView';
import UmbrellaFoodDonorStaffView from '../pages/UmbrellFoodDonor/UmbrellaFoodDonorStaffView';
import UmbrellaFoodDonorLocationView from '../pages/UmbrellFoodDonor/UmbrellaFoodDonorLocationView';
import UmbrellaFoodDonorEnvironmentImpact from '../pages/UmbrellFoodDonor/UmbrellaFoodDonorEnvironmentImpact';
import { currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite } from '../services/auth';
import { Roles } from '../models/roles';

export const umbrellaFoodDonorPaths = umbrellaFoodDonorId => [
  generatePath(routes.umbrellaFoodDonor, { id: umbrellaFoodDonorId }),
  generatePath(routes.umbrellaFoodDonor, { id: umbrellaFoodDonorId, tab: 'past-rescues' }),
  generatePath(routes.umbrellaFoodDonor, { id: umbrellaFoodDonorId, tab: 'food-donors' }),
  generatePath(routes.umbrellaFoodDonor, { id: umbrellaFoodDonorId, tab: 'staff' }),
  generatePath(routes.umbrellaFoodDonor, { id: umbrellaFoodDonorId, tab: 'profile' }),
  generatePath(routes.umbrellaFoodDonor, { id: umbrellaFoodDonorId, tab: 'environment-impact' }),
];

// the umbrella admin's own dashboard (/food-donor-dashboard), same tabs
export const umbrellaFoodDonorDashboardPaths = [
  generatePath(routes.dashboardFoodDonor),
  generatePath(routes.dashboardFoodDonor, { tab: 'past-rescues' }),
  generatePath(routes.dashboardFoodDonor, { tab: 'food-donors' }),
  generatePath(routes.dashboardFoodDonor, { tab: 'staff' }),
  generatePath(routes.dashboardFoodDonor, { tab: 'profile' }),
  generatePath(routes.dashboardFoodDonor, { tab: 'environment-impact' }),
];

const UmbrellaFoodDonorView = ({ match, renderParams }) => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const umbrellaFoodDonorId = parseInt(match.params.id || renderParams.id);
  const sites = useSelector(state => state.entities.sites);
  const sitesList = Object.values(sites.byId);
  const umbrellaFoodDonors = useSelector(state => state.entities.umbrellaFoodDonors);
  const umbrellaFoodDonor = umbrellaFoodDonors.byId[umbrellaFoodDonorId];
  // the umbrella admin's own dashboard: no route id. site staff open the umbrella page by id
  const isOwnDashboard = !match.params.id;
  const paths = isOwnDashboard ? umbrellaFoodDonorDashboardPaths : umbrellaFoodDonorPaths(umbrellaFoodDonorId);

  useEffect(() => {
    dispatch(sitesActions.fetchSitesIfNeeded());
    dispatch(foodDonorsActions.fetchFoodDonors(umbrellaFoodDonorId));
    dispatch(umbrellaFoodDonorsActions.fetchUmbrellaFoodDonorIfNeeded(umbrellaFoodDonorId));
    dispatch(umbrellaFoodDonorsActions.fetchUmbrellaFoodDonorsStaff(umbrellaFoodDonorId));
  }, [dispatch]);

  const updateUmbrellaFoodDonor = (umbrellaFoodDonorId, data) =>
    dispatch(umbrellaFoodDonorsActions.updateUmbrellaFoodDonor(umbrellaFoodDonorId, data));

  const filterRescues = (fromSiteFilter) => (rescue) => {
    const rescueSite = sitesList.find((site) => site.id === rescue.site_id);
    const siteDisabled = rescueSite?.disabled_at;

    if (fromSiteFilter === RescuesFromSiteFilterTypes.enabled) {
      return rescue.donor_id === umbrellaFoodDonorId && !siteDisabled;
    }

    if (fromSiteFilter === RescuesFromSiteFilterTypes.disabled) {
      return rescue.donor_id === umbrellaFoodDonorId && siteDisabled;
    }

    return rescue.donor_id === umbrellaFoodDonorId;
  };

  if (umbrellaFoodDonors.inflight || !umbrellaFoodDonor) {
    return <div>Loading umbrella food donors</div>;
  }

  const isAdmin = currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
    Roles.Admin,
    Roles.NationalSiteDirector,
    Roles.SiteDirector,
  ]);

  return (
    <>
      {/* the umbrella food donors list is site staff's: the umbrella's own users only get its name */}
      <Breadcrumbs aria-label="Breadcrumbs">
        {!isOwnDashboard && (
          <Link color="inherit" to={routes.umbrellaFoodDonors}>
            Umbrella Food Donors
          </Link>
        )}

        <Typography color="textPrimary">{umbrellaFoodDonor.name}</Typography>
      </Breadcrumbs>

      <AppBar position="static">
        <Tabs indicatorColor="secondary" textColor="inherit" value={paths.includes(pathname) ? pathname : false}>
          <TabLink label="Future Rescues" to={paths[0]} value={paths[0]} />
          <TabLink label="Past Rescues" to={paths[1]} value={paths[1]} />
          <TabLink label="Food Donors" to={paths[2]} value={paths[2]} />
          <TabLink label="Staff" to={paths[3]} value={paths[3]} />
          <TabLink label="Profile" to={paths[4]} value={paths[4]} />
          <TabLink label="Environmental Impact" to={paths[5]} value={paths[5]} />
        </Tabs>
      </AppBar>

      <Suspense fallback={<Loading />}>
        <Switch>
          <Route
            path={paths[0]}
            exact
            render={() => (
              <FutureRescuesListView
                umbrellaFoodDonorId={umbrellaFoodDonorId}
                umbrellaFoodDonorName={umbrellaFoodDonor?.name}
                sites={sites.byId}
                // enabled / disabled sites is a site staff concern: the umbrella's own users get the enabled ones
                filter={isOwnDashboard ? filterRescues(RescuesFromSiteFilterTypes.enabled) : filterRescues}
                displayFoodDonorFilters={false}
                showDisabledSiteFilter={!isOwnDashboard}
                showEditRescueButton={isAdmin}
                renderNamesAsLinks={false}
              />
            )}
          />
          <Route
            path={paths[1]}
            exact
            render={() => (
              <PastRescuesListView
                umbrellaFoodDonorId={umbrellaFoodDonorId}
                umbrellaFoodDonorName={umbrellaFoodDonor?.name}
                filter={isOwnDashboard ? filterRescues(RescuesFromSiteFilterTypes.all) : filterRescues}
                displayFoodDonorFilters={false}
                showDisabledSiteFilter={!isOwnDashboard}
              />
            )}
          />
          <Route
            path={paths[2]}
            exact
            render={() => (
              <Grid container>
                <Grid item xs={12}>
                  <UmbrellaFoodDonorLocationView 
                    umbrellaFoodDonorId={umbrellaFoodDonorId} 
                    umbrellaFoodDonorName={umbrellaFoodDonor?.name}
                    isOwnDashboard={isOwnDashboard}
                  />
                </Grid>
              </Grid>
            )}
          />
          <Route
            path={paths[3]}
            exact
            render={() => (
              <Grid container>
                <Grid item xs={12}>
                  <UmbrellaFoodDonorStaffView umbrellaFoodDonorId={umbrellaFoodDonorId} />
                </Grid>
              </Grid>
            )}
          />
          <Route
            path={paths[4]}
            exact
            render={() => (
              <UmbrellaFoodDonorEditForm
                umbrellaFoodDonor={umbrellaFoodDonor}
                onSubmit={values => updateUmbrellaFoodDonor(values.id, values)}
              />
            )}
          />
          <Route
            path={paths[5]}
            exact
            render={() => <UmbrellaFoodDonorEnvironmentImpact umbrellaFoodDonorId={umbrellaFoodDonorId} />}
          />
          {/* e.g. a tab remembered from another dashboard: open the first one rather than an empty page */}
          <Redirect to={paths[0]} />
        </Switch>
      </Suspense>
    </>
  );
};

export default UmbrellaFoodDonorView;
